import React, { useMemo, useRef, useState } from 'react';
import { Loader2, Upload, X, AlertTriangle, FileUp } from 'lucide-react';
import { SifaMitarbeiter } from '../../services/sifa/types';
import { upsertMitarbeiter } from '../../services/sifa/sifaService';
import { findeMitarbeiter } from '../../services/sifa/mitarbeiterMatch';
import { describeError } from '../../services/errorMessage';
import { BEISPIEL_CSV, markiereDubletten, nurNeue, parseMitarbeiterCsv } from './mitarbeiterImportLogic';
import { formatDatum } from './unterweisungModulLogic';

interface Props {
  projectId: string;
  bestand: SifaMitarbeiter[];
  onClose: () => void;
  onFertig: (angelegt: SifaMitarbeiter[]) => void;
}

/**
 * CSV-Import: Vorname;Nachname;Geburtsdatum;E-Mail;Abteilung.
 *
 * Einfuegen oder Datei waehlen, dann die Vorschau: Dubletten gegen den
 * Bestand (Regel „3 von 4") und innerhalb der Datei werden markiert und
 * NICHT angelegt; Fehlerzeilen ebenfalls nicht. Angelegt wird nacheinander —
 * ein Kunde liefert Dutzende, keine Tausende, und ein Fehler mittendrin soll
 * sagen koennen, wie viele schon drin sind.
 */
export const MitarbeiterImportDialog: React.FC<Props> = ({ projectId, bestand, onClose, onFertig }) => {
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fortschritt, setFortschritt] = useState(0);
  const [fehler, setFehler] = useState<string | null>(null);
  const dateiRef = useRef<HTMLInputElement>(null);

  const ergebnis = useMemo(() => (text.trim() ? parseMitarbeiterCsv(text) : null), [text]);
  const geprueft = useMemo(() => (ergebnis ? markiereDubletten(ergebnis.zeilen, bestand, findeMitarbeiter) : []), [ergebnis, bestand]);
  const neue = nurNeue(geprueft);

  const dateiLesen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.onerror = () => setFehler('Die Datei konnte nicht gelesen werden.');
    reader.readAsText(file);
  };

  const anlegen = async () => {
    if (laeuft || neue.length === 0) return;
    setLaeuft(true);
    setFehler(null);
    setFortschritt(0);
    const angelegt: SifaMitarbeiter[] = [];
    try {
      for (const z of neue) {
        const m = await upsertMitarbeiter(projectId, {
          vorname: z.vorname, nachname: z.nachname, geburtsdatum: z.geburtsdatum, email: z.email, abteilung: z.abteilung,
        });
        angelegt.push(m);
        setFortschritt(angelegt.length);
      }
      onFertig(angelegt);
    } catch (e) {
      setFehler(`${describeError(e)} — ${angelegt.length} von ${neue.length} wurden angelegt.`);
      if (angelegt.length > 0) onFertig(angelegt);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Mitarbeiter aus CSV importieren</h3>
            <p className="text-xs text-slate-500">Spalten: Vorname; Nachname; Geburtsdatum (TT.MM.JJJJ oder JJJJ-MM-TT); E-Mail; Abteilung — Kopfzeile optional.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => dateiRef.current?.click()} className="min-h-[40px] px-3 flex items-center gap-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl">
              <FileUp className="w-4 h-4" /> Datei wählen (.csv, .txt)
            </button>
            <button onClick={() => setText(BEISPIEL_CSV)} className="text-xs font-semibold text-slate-500 hover:underline">Beispiel einfügen</button>
            <input ref={dateiRef} type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden" onChange={dateiLesen} />
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder={BEISPIEL_CSV}
            className="w-full px-3 py-2 text-xs font-mono text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-cyan-300"
          />

          {ergebnis && ergebnis.fehler.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
              <p className="font-bold flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> Nicht lesbar (werden übersprungen):</p>
              <ul className="list-disc pl-5 space-y-0.5">{ergebnis.fehler.map((f) => <li key={f}>{f}</li>)}</ul>
            </div>
          )}

          {geprueft.length > 0 && (
            <div className="border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-xs min-w-[640px]">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-bold">Zeile</th>
                    <th className="px-2 py-1.5 text-left font-bold">Name</th>
                    <th className="px-2 py-1.5 text-left font-bold">Geburtsdatum</th>
                    <th className="px-2 py-1.5 text-left font-bold">E-Mail</th>
                    <th className="px-2 py-1.5 text-left font-bold">Abteilung</th>
                    <th className="px-2 py-1.5 text-left font-bold">Ergebnis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {geprueft.map((z) => (
                    <tr key={z.zeile} className={z.dublette || z.doppeltInDatei !== null ? 'bg-amber-50/50 text-slate-500' : ''}>
                      <td className="px-2 py-1.5">{z.zeile}</td>
                      <td className="px-2 py-1.5 font-semibold">{z.vorname} {z.nachname}</td>
                      <td className="px-2 py-1.5">{z.geburtsdatum ? formatDatum(z.geburtsdatum) : '—'}</td>
                      <td className="px-2 py-1.5">{z.email || '—'}</td>
                      <td className="px-2 py-1.5">{z.abteilung || '—'}</td>
                      <td className="px-2 py-1.5">
                        {z.dublette
                          ? <span className="text-amber-700 font-semibold">schon vorhanden: {z.dublette.vorname} {z.dublette.nachname}</span>
                          : z.doppeltInDatei !== null
                            ? <span className="text-amber-700 font-semibold">doppelt (Zeile {z.doppeltInDatei})</span>
                            : <span className="text-emerald-700 font-semibold">wird angelegt</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {fehler && <p className="text-xs text-red-700">{fehler}</p>}
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">
            {geprueft.length > 0 ? `${neue.length} neu · ${geprueft.length - neue.length} übersprungen` : 'Noch nichts eingelesen'}
            {laeuft ? ` · ${fortschritt} angelegt…` : ''}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="min-h-[40px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl">Abbrechen</button>
            <button onClick={() => void anlegen()} disabled={laeuft || neue.length === 0} className="min-h-[40px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-700 rounded-xl disabled:opacity-40">
              {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {neue.length === 1 ? '1 Person anlegen' : `${neue.length} Personen anlegen`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
